import React, { useRef } from 'react';
import PropTypes from 'prop-types';
import { useFormikContext, getIn, Formik, Form } from 'formik';
import { Grid } from '@material-ui/core';
import * as Yup from 'yup';

import { Button } from 'core/components/button';
import BaseDialog from 'core/components/base-dialog/base-dialog';
import { Label } from 'core/components/label';
import { FormField } from 'core/components/form';
import useStyles from './edit-location-details-modal.styles';

const EditLocationDetailsModal = ({ open, onClose, dwelling, dwellingLocation, fieldName, includeApt }) => {
  const classes = useStyles();
  const { setFieldValue, setFieldTouched, values } = useFormikContext();

  const startingAddress = useRef(dwellingLocation);
  const initialNoUnitNumber = useRef(getIn(values, `${dwelling.toLowerCase()}.noUnitNumber`));

  const validationSchema = Yup.object().shape({
    address: Yup.string().required('Street address is required'),
    city: Yup.string().required('City is required'),
    address2: includeApt ? Yup.string().max(50, 'Unit/apartment info cannot exceed 50 characters') : Yup.string(),
    noUnitNumber: Yup.boolean()
  });

  const initialValues = {
    address: dwellingLocation.address || '',
    city: dwellingLocation.city || '',
    address2: dwellingLocation.address2 || '',
    noUnitNumber: getIn(values, `${dwelling.toLowerCase()}.noUnitNumber`) || false
  };

  const handleSubmit = async (modalValues) => {
    setFieldValue(`${fieldName}.address`, modalValues.address.toUpperCase());
    setFieldTouched(`${fieldName}.address`, true);
    setFieldValue(`${fieldName}.city`, modalValues.city.toUpperCase());
    setFieldTouched(`${fieldName}.city`, true);
    setFieldValue('correctedAddress.address', modalValues.address.toUpperCase());
    setFieldTouched('correctedAddress.address', true);
    setFieldValue('correctedAddress.city', modalValues.city.toUpperCase());
    setFieldTouched('correctedAddress.city', true);
    if (includeApt) {
      setFieldValue(`${fieldName}.address2`, modalValues.noUnitNumber ? '' : modalValues.address2?.toUpperCase());
      setFieldTouched(`${fieldName}.address2`, true);
      setFieldValue(`${dwelling.toLowerCase()}.noUnitNumber`, modalValues.noUnitNumber);
      setFieldTouched(`${dwelling.toLowerCase()}.noUnitNumber`, true);
    }
    onClose();
  };

  const onExit = () => {
    setFieldValue(`${fieldName}.address`, startingAddress.current.address);
    setFieldValue(`${fieldName}.city`, startingAddress.current.city);
    if (includeApt) {
      setFieldValue(`${fieldName}.address2`, startingAddress.current.address2);
      setFieldValue(`${dwelling.toLowerCase()}.noUnitNumber`, initialNoUnitNumber.current);
    }
    onClose();
  };

  return (
    <BaseDialog open={open} onClose={onExit} size="md">
      <Label type="greenBig" className={classes.modalHeader}>
        Modify Address
      </Label>
      <Label type="greenSmall" className={classes.titleSecondary}>
        Change the address of the insured property as it will appear on documents
      </Label>
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
        enableReinitialize
      >
        {({ handleSubmit: formikSubmit, isValid }) => (
          <Form>
            <Grid container direction="column" className={classes.addressContainer}>
              <FormField name="address" type="string" label="street address" mode="light" xs={12} />
              {includeApt && (
                <Grid container spacing={4}>
                  <FormField
                    name="address2"
                    type="string"
                    label="Floor, unit, building, etc."
                    mode="light"
                    xs={6}
                    className={classes.addressField}
                  />
                  <Grid item className={classes.noUnitNumber} xs={6}>
                    <FormField
                      type="checkbox"
                      name="noUnitNumber"
                      mode="light"
                      label={`${dwelling} does not have an apt/unit number`}
                      fast={false}
                      item
                    />
                  </Grid>
                </Grid>
              )}
              <FormField name="city" type="string" label="city" mode="light" xs={12} />
              <FormField
                name="stateAndZip"
                type="string"
                label="state/zip"
                mode="light"
                xs={12}
                value={`${dwellingLocation.state}/${dwellingLocation.zip}`}
                disabled
              />
              <Button
                className={classes.saveBtn}
                variant="contained"
                color="primary"
                onClick={formikSubmit}
                disabled={!isValid}
              >
                Save Changes
              </Button>
            </Grid>
          </Form>
        )}
      </Formik>
    </BaseDialog>
  );
};

EditLocationDetailsModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  dwelling: PropTypes.string,
  dwellingLocation: PropTypes.object.isRequired,
  fieldName: PropTypes.string,
  includeApt: PropTypes.bool
};

EditLocationDetailsModal.defaultProps = {
  dwelling: 'Home',
  fieldName: 'home.homeLocation',
  includeApt: false
};

export default EditLocationDetailsModal;
